import type { InputHTMLAttributes } from "react";
import { forwardRef } from "react";

import * as stylex from "@stylexjs/stylex";

import { colors, genericStyles } from "../../styles/tokens.stylex";

export interface IRadioButton extends InputHTMLAttributes<HTMLInputElement> {
    label: string;
    dataTestid?: string;
}

const RadioButton = forwardRef<HTMLLabelElement, IRadioButton>(({ label, dataTestid, ...props }, ref) => (
    <label htmlFor={props.id} ref={ref} {...stylex.props(styles.wrapper)}>
        <input
            {...props}
            {...stylex.props(styles.input)}
            type="radio"
            title={label}
            data-testid={dataTestid}
        />
        <span {...stylex.props(styles.span)}>{label}</span>
    </label>
));

const styles = stylex.create({
    wrapper: {
        display: "grid",
        // eslint-disable-next-line quotes
        gridTemplateAreas: '"input text"',
        gridTemplateColumns: "1em auto",
        gap: "0 0.5em",
        alignItems: "center",
        cursor: "pointer",
        margin: 0,
    },
    input: {
        gridArea: "input",
        appearance: "none",
        backgroundColor: colors.b_Emphasis,
        margin: 0,
        cursor: "pointer",
        color: colors.gray,
        width: "1em",
        height: "1em",
        borderWidth: "0.0625em",
        borderStyle: "solid",
        borderColor: colors.gray,
        borderRadius: genericStyles.borderRadiusFullRound,
        transform: "translateY(-0.075em)",
        display: "grid",
        placeContent: "center",

        "::before": {
            content: "",
            width: "0.6em",
            height: "0.6em",
            clipPath: "polygon(14% 44%, 0 65%, 50% 100%, 100% 16%, 80% 0%, 43% 62%)",
            transform: "scale(0)",
            transformOrigin: "bottom left",
            transition: "120ms all ease-in-out",
            boxShadow: `inset 0.6em 0.6em ${colors.b_BrandMagenta}`,
        },

        ":checked::before": {
            transform: "scale(1) !important",
        },

        ":disabled": {
            cursor: "not-allowed",
        },

        ":focus": {
            borderColor: colors.gray,
        },
    },

    span: {
        gridArea: "text",
    },
});

export default RadioButton;
